import { useState } from 'react';
import {
  addDescription,
  addLabel,
  classifyOrigin,
  type Taxonomy,
} from '../lib/taxonomy';
import type { ElevationCode, ShotContext } from './types';

const ELEVATION_LABELS: Record<ElevationCode, string> = {
  front: 'Front Elevation',
  right: 'Right Elevation',
  rear: 'Rear Elevation',
  left: 'Left Elevation',
};

function elevationOf(label: string): ElevationCode | null {
  for (const [code, name] of Object.entries(ELEVATION_LABELS)) {
    if (name === label) return code as ElevationCode;
  }
  return null;
}

function splitDescription(s: string): string[] {
  return s.split(', ').map((x) => x.trim()).filter(Boolean);
}

interface Props {
  taxonomy: Taxonomy;
  defaults: Taxonomy;
  current: ShotContext;
  onSelect: (next: ShotContext) => void;
  onClose: () => void;
  onTaxonomyMutated: (next: Taxonomy) => void;
}

export function TaxonomyDrawer({
  taxonomy,
  defaults,
  current,
  onSelect,
  onClose,
  onTaxonomyMutated,
}: Props) {
  const [browsingCategory, setBrowsingCategory] = useState(current.category);
  const [browsingLabel, setBrowsingLabel] = useState(current.label);
  const [addingLabel, setAddingLabel] = useState(false);
  const [labelText, setLabelText] = useState('');
  const [addingDesc, setAddingDesc] = useState(false);
  const [descText, setDescText] = useState('');
  const [saving, setSaving] = useState(false);

  const origin = classifyOrigin(taxonomy, defaults);
  const cat = taxonomy.categories.find((c) => c.name === browsingCategory);
  const lab = cat?.labels.find((l) => l.name === browsingLabel);
  const availableLabels = cat?.labels ?? [];
  const availableDescriptions = lab?.descriptions ?? [];
  const selected = splitDescription(current.description);

  function pickCategory(name: string) {
    setBrowsingCategory(name);
    const nextCat = taxonomy.categories.find((c) => c.name === name);
    if (nextCat && nextCat.labels.length > 0) {
      setBrowsingLabel(nextCat.labels[0].name);
    } else {
      setBrowsingLabel('');
    }
  }

  function pickLabel(name: string) {
    setBrowsingLabel(name);
    const unchanged =
      name === current.label && browsingCategory === current.category;
    onSelect({
      ...current,
      category: browsingCategory,
      label: name,
      description: unchanged ? current.description : '',
      elevation: elevationOf(name),
      locked: false,
    });
  }

  function toggleDescription(d: string) {
    const currentSel = splitDescription(current.description);
    const next = currentSel.includes(d)
      ? currentSel.filter((x) => x !== d)
      : [...currentSel, d];
    onSelect({
      ...current,
      category: browsingCategory,
      label: browsingLabel,
      description: next.join(', '),
      elevation: elevationOf(browsingLabel),
      locked: false,
    });
  }

  async function submitLabel() {
    const v = labelText.trim();
    if (!v || saving) return;
    setSaving(true);
    try {
      const next = await addLabel(browsingCategory, v);
      onTaxonomyMutated(next);
      pickLabel(v);
      setLabelText('');
      setAddingLabel(false);
    } finally {
      setSaving(false);
    }
  }

  async function submitDesc() {
    const v = descText.trim();
    if (!v || saving) return;
    setSaving(true);
    try {
      const next = await addDescription(browsingCategory, browsingLabel, v);
      onTaxonomyMutated(next);
      const currentSel = splitDescription(current.description);
      const newSel = [...currentSel.filter((x) => x !== v), v];
      onSelect({
        ...current,
        category: browsingCategory,
        label: browsingLabel,
        description: newSel.join(', '),
        elevation: elevationOf(browsingLabel),
        locked: false,
      });
      setDescText('');
      setAddingDesc(false);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 bg-black/60" onClick={onClose}>
      <div
        className="absolute inset-x-0 bottom-0 flex max-h-[90vh] flex-col overflow-y-auto rounded-t-2xl bg-white p-4 pb-6 text-black"
        onClick={(e) => e.stopPropagation()}
      >
        {/* drag handle */}
        <div className="mx-auto mb-3 h-1 w-10 shrink-0 rounded-full bg-gray-300" />

        {/* Categories */}
        <div className="mb-1 text-xs font-bold uppercase tracking-wide text-gray-500">
          Category
        </div>
        <div className="-mx-4 mb-4 shrink-0 overflow-x-auto">
          <div className="flex items-center gap-2 px-4">
            {taxonomy.categories.map((c) => (
              <button
                key={c.name}
                onClick={() => pickCategory(c.name)}
                className={`shrink-0 rounded-full border px-3 py-1.5 text-sm ${
                  c.name === browsingCategory
                    ? 'border-eva-teal bg-eva-teal text-white'
                    : 'border-gray-300 bg-white'
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>
        </div>

        {/* Labels */}
        <div className="mb-1 flex items-baseline justify-between">
          <div className="text-xs font-bold uppercase tracking-wide text-gray-500">
            Label
          </div>
          <div className="text-[10px] text-gray-400">{browsingCategory}</div>
        </div>
        <div className="-mx-4 mb-4 shrink-0 overflow-x-auto">
          <div className="flex items-center gap-2 px-4">
            {availableLabels.map((l) => {
              const isMine = origin.isUserLabel(browsingCategory, l.name);
              const isActive = l.name === browsingLabel;
              return (
                <button
                  key={l.name}
                  onClick={() => pickLabel(l.name)}
                  className={`shrink-0 rounded-full border px-3 py-1.5 text-sm ${
                    isActive
                      ? 'border-eva-teal bg-eva-teal text-white'
                      : isMine
                      ? 'border-mine bg-mine/10'
                      : 'border-gray-300 bg-white'
                  }`}
                >
                  {l.name}
                </button>
              );
            })}
            {addingLabel ? (
              <div className="flex shrink-0 items-center gap-1">
                <input
                  autoFocus
                  value={labelText}
                  onChange={(e) => setLabelText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void submitLabel();
                    if (e.key === 'Escape') {
                      setAddingLabel(false);
                      setLabelText('');
                    }
                  }}
                  disabled={saving}
                  placeholder="new label"
                  className="w-36 rounded border border-gray-300 px-2 py-1 text-sm"
                />
                <button
                  onClick={() => void submitLabel()}
                  disabled={saving || !labelText.trim()}
                  className="rounded bg-eva-teal px-2 py-1 text-sm font-semibold text-white disabled:opacity-40"
                >
                  Add
                </button>
              </div>
            ) : (
              <button
                onClick={() => setAddingLabel(true)}
                className="shrink-0 rounded-full border border-dashed border-gray-400 px-3 py-1.5 text-sm text-gray-600"
              >
                + new label
              </button>
            )}
          </div>
        </div>

        {/* Descriptions */}
        <div className="mb-1 flex items-baseline justify-between">
          <div className="text-xs font-bold uppercase tracking-wide text-gray-500">
            Descriptions
          </div>
          <div className="text-[10px] text-gray-400">
            {browsingLabel}
            {selected.length > 0 && (
              <span className="ml-2 text-eva-teal">
                {selected.length} selected
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 pb-1">
          {availableDescriptions.map((d) => {
            const isSelected = selected.includes(d);
            const isMine = origin.isUserDescription(
              browsingCategory,
              browsingLabel,
              d,
            );
            return (
              <button
                key={d}
                onClick={() => toggleDescription(d)}
                className={`rounded-full border px-3 py-1.5 text-sm ${
                  isSelected
                    ? 'border-bracket-blue bg-bracket-blue text-white'
                    : isMine
                    ? 'border-mine bg-mine/10'
                    : 'border-gray-300 bg-white'
                }`}
              >
                {d}
              </button>
            );
          })}
          {addingDesc ? (
            <div className="flex items-center gap-1">
              <input
                autoFocus
                value={descText}
                onChange={(e) => setDescText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void submitDesc();
                  if (e.key === 'Escape') {
                    setAddingDesc(false);
                    setDescText('');
                  }
                }}
                disabled={saving}
                placeholder="new description"
                className="w-40 rounded border border-gray-300 px-2 py-1 text-sm"
              />
              <button
                onClick={() => void submitDesc()}
                disabled={saving || !descText.trim()}
                className="rounded bg-eva-teal px-2 py-1 text-sm font-semibold text-white disabled:opacity-40"
              >
                Add
              </button>
            </div>
          ) : (
            <button
              onClick={() => setAddingDesc(true)}
              className="rounded-full border border-dashed border-gray-400 px-3 py-1.5 text-sm text-gray-600"
            >
              + new description
            </button>
          )}
        </div>

        <button
          onClick={onClose}
          className="mt-6 shrink-0 rounded-lg bg-eva-teal py-3 text-base font-semibold text-white"
        >
          Done
        </button>
      </div>
    </div>
  );
}
